/**
 * Thin Auth0 + browser adapter for @tilly/api-client.
 *
 * This module wires the platform-specific dependencies (Auth0 token, Zustand
 * business store, window event dispatch) into the pure createApiClient factory
 * and re-exports each namespace so all existing consumer imports are unchanged.
 */
import { createApiClient, createRequestTelemetry } from '@tilly/api-client';
import { tokenService } from '@/auth/token-service';
import { useBusinessStore } from '@/stores/business';
import { trackBrowserLifecycleEvents } from '@/landing/analytics';
import { trackProductEvent } from '@/lib/analytics/telemetry';

// In local dev the Vite proxy forwards /api to localhost:8787 so we use a relative path.
// In deployed environments VITE_API_URL is set to the worker origin.
const API_BASE = import.meta.env.VITE_API_URL ? import.meta.env.VITE_API_URL + '/api' : '/api';

const _client = createApiClient({
  baseUrl: API_BASE,
  getToken: () => tokenService.getToken(),
  getBusinessId: () => useBusinessStore.getState().activeBusinessId,
  onUnauthorized: () => window.dispatchEvent(new Event('auth:unauthorized')),
  onAnalyticsEvents: trackBrowserLifecycleEvents,
  // API errors and admin writes → telemetry. An unreachable API is only worth
  // reporting while the browser thinks it is online.
  onRequestComplete: createRequestTelemetry(
    (event) => {
      if (event.name === 'api_error' && event.data.status === 0 && navigator.onLine === false)
        return;
      trackProductEvent(event.name, event.data);
    },
    { includeAdminActions: true }
  ),
});

export const publicApi = _client.publicApi;
export const adminBusinessApi = _client.adminBusinessApi;
export const adminAppointmentsApi = _client.adminAppointmentsApi;
export const adminRoutingApi = _client.adminRoutingApi;
export const adminClientsApi = _client.adminClientsApi;
export const adminTagsApi = _client.adminTagsApi;
export const adminProvidersApi = _client.adminProvidersApi;
export const adminInvitesApi = _client.adminInvitesApi;
export const providerApi = _client.providerApi;
export const publicInvitesApi = _client.publicInvitesApi;
export const adminRemindersApi = _client.adminRemindersApi;
export const conciergeApi = _client.conciergeApi;
export const adminFollowUpApi = _client.adminFollowUpApi;
export const adminAlertsApi = _client.adminAlertsApi;
export const adminRiskPolicyApi = _client.adminRiskPolicyApi;
export const schedulerApi = _client.schedulerApi;
export const adminPaymentsApi = _client.adminPaymentsApi;
export const adminMessagesApi = _client.adminMessagesApi;
export const adminSearchApi = _client.adminSearchApi;
export const adminSuggestionsApi = _client.adminSuggestionsApi;
export const adminReportsApi = _client.adminReportsApi;
export const adminEventsApi = _client.adminEventsApi;
export const adminAnalyticsApi = _client.adminAnalyticsApi;
export const businessTemplatesApi = _client.businessTemplatesApi;
export const nudgeDeliveryApi = _client.nudgeDeliveryApi;
export const authApi = _client.auth;
